import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { discardDecision, keepDecidedFrame, readDecision } from '@/lib/capture-frames';
import type { CaptureFrame } from '@/lib/capture-frames';

const FAULT_COPY: Record<string, string> = {
  resolution: 'Frame is too small. Fill more of the frame with the item.',
  exposure: 'Lighting is off. Move somewhere brighter or avoid backlight.',
  sharpness: 'Image looks soft, or the surface carries little detail to read.',
};

/**
 * CA-04: Quality gate retake (wireframes/capture-app.html #ca04). Named per
 * SC3 note 3, this screen is advisory: "Keep the frame" adds it to the
 * capture with the failing `QualityGateResult` attached, because whether a
 * failed gate blocks is open decision B1 and it is a server decision, never
 * a client one.
 *
 * The frame comes from `lib/capture-frames`, which the viewfinder fills
 * before navigating here. This screen used to draw the words "captured frame
 * preview" in place of the photograph and queue `new Blob()` in place of its
 * bytes, so the operator judged a frame they could not see and the capture
 * then failed its whole retry budget on a 400. Both halves came from the same
 * missing thing, which was any route for the pixels to travel.
 *
 * Neither choice sends anything. A capture carries up to three frames and the
 * operator is still building it, so both routes lead back to the viewfinder
 * and the queueing happens there, once, over the whole set.
 */
export function QualityRetake() {
  const navigate = useNavigate();
  const { jobId = '' } = useParams();
  const viewfinderPath = `/jobs/${encodeURIComponent(jobId)}/capture`;
  const [frame] = useState<CaptureFrame | null>(() => readDecision()?.frame ?? null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!frame) {
      // Nothing to decide about, which a reload causes: the bytes lived in
      // memory for one decision. Sending the operator back to the viewfinder
      // is honest, where queueing an empty capture was not.
      navigate(viewfinderPath, { replace: true });
      return;
    }
    const url = URL.createObjectURL(frame.bytes);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [frame, navigate, viewfinderPath]);

  if (!frame) return null;

  const failedChecks = frame.qualityResult.checks.filter((check) => !check.passed);

  function keepAnyway() {
    const outcome = keepDecidedFrame();
    // The operator is standing in front of the item, so the route back is the
    // viewfinder, where the frame now sits in the strip with the others and
    // the whole capture queues in one action. What became of the frame travels
    // with them: a full capture takes no fourth, and saying nothing would send
    // them on believing they had it.
    navigate(viewfinderPath, {
      replace: true,
      // Nothing held is the reload case, which this screen already redirects
      // on, so it carries no message rather than an invented one.
      ...(outcome === 'nothing-held' ? {} : { state: { keptFromRetake: outcome } }),
    });
  }

  function retake() {
    discardDecision();
    navigate(viewfinderPath, { replace: true });
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 sm:max-w-lg">
      {/*
        The frame sizes itself from the photograph rather than from a 3:4 box,
        because a capture is whatever shape the operator shot and letterboxing
        it inside the viewfinder's ratio shows less of it. Height is capped in
        viewport units so both choices stay on screen beside it: an operator
        who has to scroll to find "Keep the frame" has been handed the retake
        by the layout rather than by the photograph.
      */}
      <div className="mx-auto flex w-fit min-h-[30vh] max-w-full items-center justify-center overflow-hidden rounded-card border border-border bg-black/20 px-6">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="The frame you just captured, for your retake decision"
            className="max-h-[42vh] w-auto max-w-full object-contain sm:max-h-[50vh]"
          />
        ) : (
          <span className="text-xs text-muted">loading the frame</span>
        )}
      </div>

      <div className="rounded-card border border-warning p-4">
        {failedChecks.map((check) => (
          <div key={check.name} className="mb-2 last:mb-0">
            <p className="font-medium text-warning">
              {FAULT_COPY[check.name] ?? `${check.name} check failed`}
            </p>
          </div>
        ))}
      </div>

      <Button size="lg" onClick={retake}>
        Retake
      </Button>
      <Button variant="ghost" size="lg" onClick={keepAnyway}>
        Keep the frame
      </Button>
    </div>
  );
}
